import { Box, Button, Grid2 as Grid } from "@mui/material"
import Stack from "@mui/material/Stack"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useTranslation } from "react-i18next"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import { useQsFilters } from "../hooks/filters"
import { useProduzioneFileDigitaleCreate, useProduzioniFileDigitaliList, useProduzioneFileDigitaleDelete, useProduzioneFileDigitaleUpdate } from "../hooks/produzioniFileDigitali"
import { ProduzioneFileDigitale } from "../types"
import ProduzioneFileDigitalCard from "../components/ProduzioneFileDigitaleCard"
import DialogConfirm from "../components/DialogConfirm"
import { useSnackbarMessage } from "../hooks/snackbar"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useIsMobile } from "../hooks/mobile"
import ProduzioneFileDigitaleForm from "../components/forms/ProduzioneFileDigitaleForm"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  formato: params.get("formato") ?? "",
  elemento: params.get("elemento") ?? "",
})

type CopieProps = {
  fileDigitaleId: number
}

export default function Copie({ fileDigitaleId }: CopieProps) {
  const { t } = useTranslation()
  const { filters } = useQsFilters(initFilters)
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const { data } = useProduzioniFileDigitaliList({
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
    file_digitale: fileDigitaleId.toString(),
  })

  const { mutateAsync: create } = useProduzioneFileDigitaleCreate()
  const { mutateAsync: update } = useProduzioneFileDigitaleUpdate()
  const { mutateAsync: remove } = useProduzioneFileDigitaleDelete()

  const [createModalState, createModalActions] = useModalTrigger()
  const [updateModalState, updateModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  return (
    <Layout>
      <Stack sx={{ minHeight: "100%" }}>
        <Box
          sx={{
            textAlign: "right",
            position: "sticky",
            top: 0,
            zIndex: 1,
            justifyContent: "flex-end",
            backgroundColor: "grey.100",
            borderBottom: 1,
            borderColor: "grey.300",
          }}
          px={3}
          py={1}
        >
          {userCanWriteAndDelete && !isMobile && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("produzione.cta_aggiungi")}
            </Button>
          )}
        </Box>

        <Box flexGrow={1} px={3} pb={3} pt={1}>
          <Grid container spacing={2}>
            {data?.results.map((produzione) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                  lg: 6,
                  xl: 4,
                }}
                key={produzione.id}
              >
                <ProduzioneFileDigitalCard
                  produzione={produzione}
                  onUpdate={() => {
                    updateModalActions.open(produzione)
                  }}
                  onDelete={() => {
                    deleteModalActions.open(produzione)
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>

      <DialogForm<ProduzioneFileDigitale>
        open={createModalState.isOpen}
        hasDoubleLanguage
        hasCancel
        handleClose={createModalActions.close}
        title={t("produzione.cta_aggiungi")}
        FormComponent={ProduzioneFileDigitaleForm}
        save={(values: ProduzioneFileDigitale) => {
          const newValues = {
            ...values,
            file_digitale: fileDigitaleId,
          }
          return create(newValues as ProduzioneFileDigitale).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<ProduzioneFileDigitale>
        open={updateModalState.isOpen}
        hasDoubleLanguage
        hasCancel
        handleClose={updateModalActions.close}
        title={t("produzione.cta_modifica")}
        FormComponent={ProduzioneFileDigitaleForm}
        item={updateModalState.value}
        save={(values: ProduzioneFileDigitale) => {
          return update(values).then(() => {
            updateModalActions.close()
            showMessage(t("produzione.esito_modifica_ok"))
          })
        }}
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("copia.cta_elimina")}
        subtitle={
          <span>
            {t("copia.cta_subtitle_elimina")} <b>"{deleteModalState.value?.data}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id).then(() => {
            deleteModalActions.close()
            showMessage(t("copia.esito_elimina_ok"))
          }).catch(() => {
            deleteModalActions.close()
            showMessage(t("general.esito_elimina_errore"), "error")
          })
        }}
      />
    </Layout>
  )
}
